import { Spacing } from '@persona-id/design-tokens';
import dedent from 'dedent';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import IntegrationStep from 'dashboard/components/IntegrationStep';
import TemplateSelector from 'dashboard/components/TemplateSelector/TemplateSelector';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Template } from 'dashboard/models/template';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Tab, TabContainer } from 'design-system/components/TabBar';
import { Label, Paragraph } from 'design-system/components/Text';
import MobileSDKsImage from 'images/dashboard/getting-started/mobile-sdks.svg';

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

const ListItem = styled('li')`
  margin: ${Spacing[2]} 0px;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

interface DashboardGettingStartedMobileSDKsViewProps {
  selectedTemplate: Template | InquiryTemplate | undefined;
  templates: Array<Template | InquiryTemplate>;

  handleTemplateSelect: (templateId: string) => void;
}

const DashboardGettingStartedMobileSDKsView = ({
  handleTemplateSelect,
  selectedTemplate,
  templates,
}: DashboardGettingStartedMobileSDKsViewProps) => {
  const [selectedDevice, setSelectedDevice] = useState('android');

  return (
    <>
      <IntegrationHeroCard title='Mobile SDKs' image={MobileSDKsImage}>
        With the mobile SDK integration method, you can place an information collection experience
        (configurable through an Inquiry Template) in your iOS or Android application, and
        dynamically create an individual Inquiry to later review the associated information
        collected and verification results.
      </IntegrationHeroCard>
      <Card>
        <Card.Body>
          <Card.Title>How to use this method</Card.Title>
          <TabContainer aria-label='getting-started__device-tab-bar'>
            <Tab
              id='getting-started-mobile-sdks-view__tab--android'
              data-test='getting-started-mobile-sdks-view__tab--android'
              selected={selectedDevice === 'android'}
              onClick={() => setSelectedDevice('android')}
            >
              Android
            </Tab>
            <Tab
              id='getting-started-mobile-sdks-view__tab--ios'
              data-test='getting-started-mobile-sdks-view__tab--ios'
              selected={selectedDevice === 'ios'}
              onClick={() => setSelectedDevice('ios')}
            >
              iOS
            </Tab>
            <Tab
              id='getting-started-mobile-sdks-view__tab--react-native'
              data-test='getting-started-mobile-sdks-view__tab--react-native'
              selected={selectedDevice === 'react-native'}
              onClick={() => setSelectedDevice('react-native')}
            >
              React Native
            </Tab>
          </TabContainer>
          <Spacer height={1} />
          <IntegrationStep title='Check requirements and setup' stepNumber={1}>
            {selectedDevice === 'android' && (
              <Paragraph color='primary'>
                Read our{' '}
                <a
                  href='https://docs.withpersona.com/docs/android-sdk-v2-integration-guide#requirements---make-sure-the-sdk-is-compatible'
                  target='_blank'
                  rel='noreferrer'
                >
                  compatibility requirements
                </a>{' '}
                for Android apps and{' '}
                <a
                  href='https://docs.withpersona.com/docs/android-sdk-v2-integration-guide#dependencies---adding-persona-to-your-project'
                  target='_blank'
                  rel='noreferrer'
                >
                  add Persona to your project
                </a>
                .
              </Paragraph>
            )}
            {selectedDevice === 'ios' && (
              <Paragraph color='primary'>
                Read our{' '}
                <a
                  href='https://docs.withpersona.com/docs/ios-sdk-v2-integration-guide#requirements---make-sure-the-sdk-is-compatible'
                  target='_blank'
                  rel='noreferrer'
                >
                  compatibility requirements
                </a>{' '}
                for iOS apps and{' '}
                <a
                  href='https://docs.withpersona.com/docs/ios-sdk-v2-integration-guide#install-via-swift-package-manager'
                  target='_blank'
                  rel='noreferrer'
                >
                  install the SDK
                </a>
                .
              </Paragraph>
            )}
            {selectedDevice === 'react-native' && (
              <Paragraph color='primary'>
                Our React Native SDK allows you to implement for either iOS or Android using a
                wrapper that’s built using the React framework. Read the{' '}
                <a
                  href='https://docs.withpersona.com/docs/react-native-sdk-integration#installation'
                  target='_blank'
                  rel='noreferrer'
                >
                  compatibility requirements and install the SDK
                </a>{' '}
                .
              </Paragraph>
            )}
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Select your Inquiry Template' stepNumber={2}>
            <Paragraph color='primary'>
              Select the Inquiry Template you would like to use from the dropdown below. The Inquiry
              Template will determine what information is collected and which Verifications you’d
              like to run on the data that is collected.
            </Paragraph>
            <Spacer height={1} />
            <Paragraph color='primary'>
              If you would like to create a new Inquiry Template (and you subscribe to a plan that
              allows you to create your own Inquiry Templates), open the{' '}
              <Link to='/dashboard/inquiry-templates' target='_blank'>
                Inquiry Templates
              </Link>{' '}
              page in your Dashboard and then come back to this page.
            </Paragraph>
            <Spacer height={1} />
            {templates.length >= 1 && selectedTemplate ? (
              <TemplateSelector
                handleTemplateSelect={handleTemplateSelect}
                templates={templates}
                value={selectedTemplate.id}
                label='Inquiry Template'
                size='sm'
              />
            ) : (
              <Paragraph>Loading... try refreshing the page!</Paragraph>
            )}
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Build and launch the Inquiry' stepNumber={3}>
            {selectedDevice === 'android' && (
              <>
                <Paragraph color='primary'>
                  The Inquiry flow is initiated with a builder pattern based on either Inquiry
                  Template ID, Inquiry Template Version, or Inquiry ID. You can use the code below
                  to launch the Inquiry experience.
                </Paragraph>
                <Spacer height={2} />
                <Label size='md' weight='bold'>
                  Kotlin
                </Label>
                <Spacer height={1} />
                {/* prism-react-renderer doesn't support these languages currently, so just using JSON for now */}
                {selectedTemplate && (
                  <CodeHighlight language='json'>
                    {dedent`
                    // the template ID is based on what you selected in the dropdown above
                    val TEMPLATE_ID = "${selectedTemplate.id}"
                    // ...

                    val inquiry = Inquiry.fromTemplate(TEMPLATE_ID)
                      .environment(Environment.SANDBOX)
                      .build()
                    // ...

                    getInquiryResult.launch(inquiry)
                  `}
                  </CodeHighlight>
                )}
                <Spacer height={3} />
                <Label size='md' weight='bold'>
                  Java
                </Label>
                <Spacer height={1} />
                {selectedTemplate && (
                  <CodeHighlight language='json'>
                    {dedent`
                    // the template ID is based on what you selected in the dropdown above
                    static final String TEMPLATE_ID = "${selectedTemplate.id}";
                    // ...

                    Inquiry inquiry = Inquiry.fromTemplate(TEMPLATE_ID)
                      .environment(Environment.SANDBOX)
                        .build();
                    // ...

                    getInquiryResult.launch(inquiry);
                  `}
                  </CodeHighlight>
                )}
              </>
            )}
            {selectedDevice === 'ios' && (
              <>
                <Paragraph color='primary'>
                  The Inquiry flow is initiated with an <code>InquiryConfiguration</code>. This
                  configuration can be initialized with either Inquiry Template ID, Inquiry Template
                  Version, or Inquiry ID.
                </Paragraph>
                <Spacer height={1} />
                <Paragraph color='primary'>
                  This starts the Inquiry flow and takes control of the user interface. Once the
                  flow completes the control of the user interface is returned to the app and the
                  appropriate delegate method will be called.
                </Paragraph>
                <Spacer height={2} />
                <Label size='md' weight='bold'>
                  Swift
                </Label>
                <Spacer height={1} />
                {selectedTemplate && (
                  <CodeHighlight language='json'>
                    {dedent`
                    class MyViewController: UIViewController {
                      // This is hooked up to a button which starts the flow
                      @IBAction private func buttonTapped(_ sender: UIButton) {
                        let config = InquiryConfiguration(templateId: "${selectedTemplate.id}")

                        // Create the inquiry with the view controller
                        // as the delegate and presenter.
                        Inquiry(config: config, delegate: self).start(from: self)
                      }
                    }
                  `}
                  </CodeHighlight>
                )}
              </>
            )}
            {selectedDevice === 'react-native' && (
              <>
                <Paragraph color='primary'>
                  The code below launches the Inquiry flow. It can be initialized with either an
                  Inquiry Template ID or an Inquiry ID.
                </Paragraph>
                <Spacer height={1} />
                <Paragraph color='primary'>
                  This starts the Inquiry flow and takes control of the user interface. Once the
                  flow completes, the control of the user interface is returned to the app and the
                  appropriate callbacks are called.
                </Paragraph>
                <Spacer height={2} />
                <Label size='md' weight='bold'>
                  React Native
                </Label>
                <Spacer height={1} />
                {selectedTemplate && (
                  <CodeHighlight language='jsx'>
                    {/* not using dedent for this code block as a bug prevents the escape characters from working properly */}
                    {`import Inquiry, {Environment} from 'react-native-persona';

<Button
title="Start Inquiry"
onPress={() => {
  Inquiry2.fromTemplate('${selectedTemplate.id}')
    .environment(Environment2.SANDBOX)
    .onComplete((inquiryId, status, fields) =>
      Alert.alert('Complete', \`Inquiry \${inquiryId} completed with status "\${status}."\`,),
    )
    .onCanceled((inquiryId, sessionToken) =>
      Alert.alert('Canceled', \`Inquiry \${inquiryId} was cancelled\`),
    )
    .onError(error => Alert.alert('Error', error.message))
    .build()
    .start();
}}
/>`}
                  </CodeHighlight>
                )}
              </>
            )}
            <Spacer height={3} />
            <Paragraph color='primary'>
              Make sure to set the{' '}
              <b>
                <code>environment</code>
              </b>{' '}
              properly! Use <code>sandbox</code> when testing, as this will return dummy data, save
              data to your sandbox environment, and usage will not count toward billing. Use{' '}
              <code>production</code> for live Inquires.
            </Paragraph>
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Optimize' stepNumber={4}>
            <Paragraph color='primary'>
              That’s it — congrats! Now you’ll want to test the experience and see if you would like
              to do any additional optimizations. Some common optimizations to consider include:
            </Paragraph>
            <UnorderedList>
              <ListItem>
                Making changes on the client to make the flow fit your brand and theme
              </ListItem>
              {(selectedDevice === 'android' || selectedDevice === 'ios') && (
                <ListItem>
                  Using Inquiry ID instead of Inquiry Template ID for Android or InquiryDelegate for
                  iOS to handle edge cases such as the user canceling the flow or resuming an
                  Inquiry that was previously started by a specific user
                </ListItem>
              )}
              <ListItem>
                Specifying your user ID as the Persona Reference ID so you can link all Inquiries
                from one individual to a Persona Account
              </ListItem>
              <ListItem>
                Using the <code>fields</code> parameter to dynamically present Inquiry field values
                such as name or birthday so your users just need to confirm that those values are
                correct
              </ListItem>
              {(selectedDevice === 'android' || selectedDevice === 'ios') && (
                <ListItem>
                  Adding custom styling for further brand consistency or device-specific features
                  like dark mode
                </ListItem>
              )}
              <ListItem>... and more!</ListItem>
            </UnorderedList>
            <Paragraph color='primary'>
              Learn about these optimizations{' '}
              <a
                href='https://docs.withpersona.com/docs/mobile-sdks-v2'
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>
              .
            </Paragraph>
          </IntegrationStep>
        </Card.Body>
      </Card>
    </>
  );
};

export default DashboardGettingStartedMobileSDKsView;
